import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import styles from "./E3ComboboxCheckList.module.css";
import { getChildrenCommonCode } from "e3-common/utils/commonCodeUtil";
import useFetch from "e3-common/hooks/useFetch";

// import { getChildrenCommonCode } from '@/utils/commonCodeUtil';
// import useFetch from "e3-common/hooks/useFetch";

/**
 * E3Combobox
 * @type React.FC<E3ComboboxPropTypes>
 */
const E3ComboboxCheckList = React.forwardRef((props, ref) => {
  const { request } = useFetch();
  const refSelect = useRef();
  const [isOpen, setIsOpen] = useState(false); // 드롭다운 열기 상태
  const [selectedItems, setSelectedItems] = useState([]);

  const [currState, setCurrState] = useState({
    disabled: props.disabled,
    selectedIndex: 0,
    selectedValue: "",
    selectedData: null,
    dataSource: [],
    selectedItems: [],
  });

  Object.keys(props).forEach((p) => {
    if (!Object.keys(E3ComboboxPropTypes).includes(p)) {
      console.error(`E3Combobox: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  React.useImperativeHandle(ref, () => ({
    getValue() {
      return selectedItems
        .filter((item) => item.value !== "all")
        .map((item) => item.value); // 선택된 항목들의 value 반환
    },
    setValue(newValues) {
      if (!currState.dataSource || currState.dataSource.length === 0) {
        // dataSource 준비될 때까지 대기
        setTimeout(() => setValue(newValues), 50);
        return;
      }

      // 기존 로직
      const newselectedItems = newValues
        .map((val) => {
          const foundItem = currState.dataSource.find(
            (item) => item[props.valueId]?.toString().trim() === val.trim()
          );
          return foundItem
            ? {
                value: foundItem[props.valueId],
                label: foundItem[props.labelId],
              }
            : null;
        })
        .filter((item) => item !== null);

      setSelectedItems(newselectedItems);
      setCurrState((prev) => ({
        ...prev,
        selectedItems: newselectedItems,
      }));

      if (props.onChange) props.onChange(newselectedItems);
    },
    // setValue(v) {
    //   const event = { target: { value: v } };
    //   _setValue(event);
    // },
    getSelectedData() {
      const { selectedData } = currState;
      return selectedData;
    },
    getDataSource() {
      const { dataSource } = currState;
      return dataSource;
    },
    setDataBindingByAPI(url, method, body, _setVal = "", _callback) {
      _getDataFromAPIAndSet(url, method, body, _setVal, _callback);
    },
    clear() {
      // 초기 상태로 복원 (선택 항목을 비운다)
      setSelectedItems([]);
      setCurrState((prevState) => ({
        ...prevState,
        selectedValue: "", // 선택된 값 초기화
        selectedData: null, // 선택된 데이터 초기화
      }));
      setIsOpen(false);
    },
    setDataBinding(source, _setVal = "", _callback) {
      _setDataBinding(source, _setVal, _callback);
    },
    enable() {
      setCurrState((prev) => ({
        ...prev,
        disabled: false,
      }));
    },
    disable() {
      setCurrState((prev) => ({
        ...prev,
        disabled: true,
      }));
    },

    updateState(stateName, newValue) {
      if (Object.keys(currState).includes(stateName)) {
        setCurrState((prev) => ({
          ...prev,
          [stateName]: newValue,
        }));
      } else {
        console.error(
          "E3Combobox: State에 지정되지 않은 값을 업데이트 시도하였습니다. 잘못된 시도입니다."
        );
      }
    },
  }));

  const _getDataFromAPIAndSet = async (
    url,
    method,
    body,
    _setVal = "",
    _callback
  ) => {
    const response = await request(
      url,
      { method: method ?? "GET", ...(body && { body: body }) },
      { showConfirm: false, showLoad: false }
    );
    if (response) {
      _setDataBinding(response, _setVal, _callback);
    }
  };

  // useEffect(() => {
  //   if (props.onChange) {
  //     props.onChange(selectedItems); // selectedItems가 업데이트된 후에 호출
  //   }
  // }, [selectedItems, props.onChange]);

  useEffect(() => {
    if (props.dataSource) {
      _setDataBinding(props.dataSource);
      return;
    }

    const getCommonCodesAndSet = async (parentCodeId) => {
      const codes = await getChildrenCommonCode(parentCodeId);
      if (codes?.length > 0) {
        _setDataBinding(codes);
      }
      return;
    };
    if (props.parentCodeId) {
      getCommonCodesAndSet(props.parentCodeId);
      return;
    }

    if (props.url) {
      _getDataFromAPIAndSet(props.url, props.apiMethod, props.apiBody);
    }
  }, []);

  const _addAll = (data = []) => {
    const all = {
      [props.valueId]: "all", // 전체 항목의 값, 임의로 "all"로 설정
      [props.labelId]: props.addAllPlaceholder || "전체", // 전체 항목의 라벨, 기본값은 '전체'
    };

    return [all, ...data]; // 데이터 앞에 "전체" 항목 추가
  };

  const _setDataBinding = (source, _setVal = "", _callback) => {
    let data = source || [];
    if (props.addAll) {
      data = _addAll(data);
    }
    // if (props.addBlank) {
    //   data = _addBlank(data);
    // }

    // if (props.addText) {
    //   data = _addText(data, props.addText);
    // }

    const defaultSelected = props.defaultValue
      ? data?.findIndex((x, idx) => {
          return (
            x[props.valueId] === props.defaultValue ||
            idx === Number(props.defaultValue)
          );
        })
      : 0;

    const setVal = _setVal
      ? data?.findIndex((x, idx) => {
          return x[props.valueId] === _setVal || idx === Number(_setVal);
        })
      : -1;

    const selectedData =
      data?.length > 0 ? data[setVal !== -1 ? setVal : defaultSelected] : null;
    const selectedValue = selectedData ? selectedData[props.valueId] : "";

    setCurrState((prev) => ({
      ...prev,
      selectedData: selectedData,
      selectedValue: selectedValue,
      dataSource: data,
    }));

    if (_callback > "") _callback(this);
  };

  //
  useEffect(() => {
    if (props.onChange) {
      props.onChange(selectedItems); // selectedItems가 업데이트된 후에 호출
    }
    //console.log("Selected Items:", selectedItems);
  }, [selectedItems]);

  const selectedDisplayItems = selectedItems
    .filter((item) => item.value !== "all")
    .map((item) => item.label); // Extract labels for display

  const handleCheckboxChange = (value, label) => {
    setSelectedItems((prevSelected) => {
      const itemExists = prevSelected.find((item) => item.value === value);

      // "전체" 체크박스를 클릭했을 때
      if (value === "all") {
        if (itemExists) {
          // "전체"가 이미 선택된 상태라면, 선택 해제
          return [];
        } else {
          // "전체"가 선택되지 않은 상태라면, 모든 항목 선택
          return currState.dataSource.map((item) => ({
            value: item[props.valueId],
            label: item[props.labelId],
          }));
        }
      }

      // 일반 체크박스를 클릭했을 때
      if (itemExists) {
        // 이미 선택된 항목이라면 해당 항목을 제거
        return prevSelected.filter((item) => item.value !== value);
      } else {
        // 새로운 항목을 선택된 항목에 추가
        return [...prevSelected, { value, label }];
      }
    });
  };

  return (
    <div className={styles.comboBoxContainer}>
      <div
        className={`${styles.comboBoxHeader} ${styles.e3Select}  ${
          currState.disabled ? styles.disabled : ""
        }`}
        onClick={() => !currState.disabled && setIsOpen(!isOpen)}
      >
        <span>
          {selectedDisplayItems.length > 0
            ? selectedDisplayItems.join(", ")
            : currState.disabled
            ? ""
            : "Select Options"}
        </span>
        <span>{!currState.disabled ? (isOpen ? "▲" : "▼") : ""}</span>
      </div>
      {isOpen && (
        <div className={styles.comboBoxDropdown}>
          {currState.dataSource.map((item, i) => (
            <label key={i} className={styles.checkboxOption}>
              <input
                type="checkbox"
                checked={selectedItems.some(
                  (selectedItem) => selectedItem.value === item[props.valueId]
                )}
                onChange={() =>
                  handleCheckboxChange(item[props.valueId], item[props.labelId])
                }
              />
              {item[props.labelId]}
            </label>
          ))}
        </div>
      )}
    </div>
  );
});

const E3ComboboxPropTypes = {
  addBlank: PropTypes.bool,
  addAll: PropTypes.bool,
  addAllPlaceholder: PropTypes.string,
  addText: PropTypes.string,
  valueId: PropTypes.string,
  labelId: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  parentCodeId: PropTypes.string,
  url: PropTypes.string,
  apiMethod: PropTypes.string,
  apiBody: PropTypes.object,
  dataSource: PropTypes.array,
  controlWidth: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
};

E3ComboboxPropTypes.propTypes = E3ComboboxPropTypes;
E3ComboboxPropTypes.defaultProps = {
  addBlank: false,
  addAll: false,
  addAllPlaceholder: "전체",
  addText: null,
  valueId: "codeId",
  labelId: "codeName",
  disabled: false,
  onChange: null,
  parentCodeId: null,
  url: null,
  apiMethod: null,
  apiBody: null,
  dataSource: null,
  controlWidth: null,
  defaultValue: "",
  className: "",
};
E3ComboboxCheckList.displayName = "E3ComboboxCheckList";
export default E3ComboboxCheckList;
